import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, RefreshCw, Workflow } from 'lucide-react';
import { toast } from 'sonner';
import { workflowsApi } from '../services/workflowsApi';
import WorkflowCanvasDesigner from '../components/workflow/WorkflowCanvasDesigner';
import logger from '../utils/logger';

const WorkflowManager = () => {
  const [doctypes, setDoctypes] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDoctype, setFilterDoctype] = useState('all');
  const [designerOpen, setDesignerOpen] = useState(false);
  const [designerRow, setDesignerRow] = useState(null);

  const loadDoctypes = useCallback(async () => {
    try {
      const r = await workflowsApi.listDoctypes();
      setDoctypes(r.data || []);
    } catch (err) {
      logger.error('Failed to load doctypes', err);
    }
  }, []);

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await workflowsApi.list({
        ...(filterDoctype !== 'all' ? { doctype: filterDoctype } : {}),
      });
      setWorkflows(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [filterDoctype]);

  useEffect(() => { loadDoctypes(); }, [loadDoctypes]);
  useEffect(() => { loadWorkflows(); }, [loadWorkflows]);

  const openCreate = () => { setDesignerRow(null); setDesignerOpen(true); };
  const openEdit = (row) => { setDesignerRow(row); setDesignerOpen(true); };

  const handleDelete = async (row) => {
    if (!window.confirm(
      `Delete workflow '${row.name}' on doctype '${row.doctype}'?\n` +
      `Existing records will keep their current status but transitions will stop being validated.`
    )) return;
    try {
      await workflowsApi.delete(row.workflow_id);
      toast.success('Deleted');
      loadWorkflows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="workflow-manager-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Workflow className="w-7 h-7 text-orange-600" />
              Workflow Designer
            </h1>
            <p className="text-gray-600 mt-1">
              Visually design state machines &amp; role-based transitions for any DocType
              on a drag-and-drop canvas. Each workflow binds to a state field (default <code>status</code>).
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadWorkflows} data-testid="wf-refresh">
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
            <Button onClick={openCreate} data-testid="wf-new-btn">
              <Plus className="w-4 h-4 mr-1" />New Workflow
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="pt-4 grid grid-cols-3 gap-3">
            <div className="col-span-2" />
            <Select value={filterDoctype} onValueChange={setFilterDoctype}>
              <SelectTrigger data-testid="wf-filter-doctype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All DocTypes</SelectItem>
                {doctypes.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{workflows.length} Workflow(s)</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading…</div>
            ) : workflows.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                No workflows yet. Click <strong>New Workflow</strong> to design one on the canvas.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>DocType</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>State Field</TableHead>
                    <TableHead>States</TableHead>
                    <TableHead>Transitions</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workflows.map((row) => (
                    <TableRow key={row.workflow_id} data-testid={`wf-row-${row.name}`}>
                      <TableCell><Badge variant="outline">{row.doctype}</Badge></TableCell>
                      <TableCell className="font-medium">{row.name}</TableCell>
                      <TableCell className="font-mono text-xs">{row.state_field}</TableCell>
                      <TableCell>{(row.states || []).length}</TableCell>
                      <TableCell>{(row.transitions || []).length}</TableCell>
                      <TableCell>
                        <Badge variant={row.active ? 'default' : 'secondary'}>
                          {row.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(row)} data-testid={`wf-edit-${row.name}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(row)} data-testid={`wf-delete-${row.name}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <WorkflowCanvasDesigner
          open={designerOpen}
          editRow={designerRow}
          doctypes={doctypes}
          onClose={() => setDesignerOpen(false)}
          onSaved={loadWorkflows}
        />
      </div>
    </Layout>
  );
};

export default WorkflowManager;
